import React from 'react'
import { Avatar } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { getActiveSegmentsFromScore, getColorFromScore } from '../../functions/user-insights/energy-visualization'
import './energy-avatar.less'

interface EnergyAvatarProps {
  id: string
  avatarUrl?: string
  name: string
  energyScore?: number // between 0 and 1, undefined if not calculated
  size?: number
  type?: 'small' | 'default' | 'large'
}

export const EnergyAvatar: React.FC<EnergyAvatarProps> = ({
  avatarUrl,
  energyScore,
  size = 40,
  type = 'default',
}) => {
  let dashLineWidth = 6
  // 'small' and 'default' types intentionally share dashLineWidth = 6; only 'large' uses a wider dash
  if (type === 'large') {
    dashLineWidth = 9
  }
  const containerSize = size // Make container larger to accommodate extended bars
  const totalSegments = 12

  // Handle undefined energy score
  let activeSegments = 0
  let scoreInHundreds = 0
  let segmentColor = '#d9d9d9'

  if (energyScore !== undefined) {
    // Convert to 0-100 scale if needed
    scoreInHundreds = energyScore > 1 ? energyScore : energyScore * 100

    // Get the number of active segments based on score ranges
    activeSegments = getActiveSegmentsFromScore(scoreInHundreds)

    // Get the color based on energy score
    segmentColor = getColorFromScore(scoreInHundreds)
  }

  const inactiveColor = '#d9d9d9'

  // Determine opacity based on whether energy score is calculated
  const ringOpacity = energyScore !== undefined ? 1 : 0.05

  return (
    <div
      className="energy-avatar-container"
      style={{
        width: `${containerSize}px`,
        height: `${containerSize}px`,
        position: 'relative',
        display: 'inline-block',
        flexShrink: 0,
      }}
    >
      {/* Energy bars */}
      <svg
        width={containerSize}
        height={containerSize}
        className="energy-rings"
        style={{
          position: 'absolute',
          top: '0px',
          left: '0px',
          zIndex: 1,
          display: 'block',
          opacity: ringOpacity,
        }}
        viewBox={`0 0 ${containerSize} ${containerSize}`}
      >
        {Array.from({ length: totalSegments }, (_, i) => {
          const angle = (360 / totalSegments) * i - 90 // Start from top
          const segmentWidth = 22 // Wider segments with minimal gaps
          const startAngle = angle - segmentWidth / 2
          const endAngle = angle + segmentWidth / 2
          const radius = containerSize / 2 - 2
          const innerRadius = radius - dashLineWidth

          const isActive = i < activeSegments
          const color = isActive ? segmentColor : inactiveColor

          const startAngleRad = (startAngle * Math.PI) / 180
          const endAngleRad = (endAngle * Math.PI) / 180

          const centerX = containerSize / 2
          const centerY = containerSize / 2

          const x1 = centerX + Math.cos(startAngleRad) * innerRadius
          const y1 = centerY + Math.sin(startAngleRad) * innerRadius
          const x2 = centerX + Math.cos(startAngleRad) * radius
          const y2 = centerY + Math.sin(startAngleRad) * radius
          const x3 = centerX + Math.cos(endAngleRad) * radius
          const y3 = centerY + Math.sin(endAngleRad) * radius
          const x4 = centerX + Math.cos(endAngleRad) * innerRadius
          const y4 = centerY + Math.sin(endAngleRad) * innerRadius

          return (
            <path
              key={i}
              d={`M ${x1} ${y1} L ${x2} ${y2} A ${radius} ${radius} 0 0 1 ${x3} ${y3} L ${x4} ${y4} A ${innerRadius} ${innerRadius} 0 0 0 ${x1} ${y1} Z`}
              style={{ fill: color }}
            />
          )
        })}
      </svg>

      {/* Background circle for avatar */}
      <div
        className="energy-avatar-background"
        style={{
          width: `${size - dashLineWidth * 2}px`,
          height: `${size - dashLineWidth * 2}px`,
        }}
      />

      {/* Avatar in center */}
      <div
        className="avatar-container"
        style={{
          width: `${size}px`,
          height: `${size}px`,
        }}
      >
        <Avatar
          size={size - dashLineWidth * 2}
          icon={<UserOutlined />}
          src={avatarUrl}
          shape="circle"
        />
      </div>
    </div>
  )
}
